import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { selectFolderItems } from "../../../common/Sidebar/redux/selectors";
import {
  clearRagObjects,
  toggleAccordion,
} from "../../../common/Sidebar/redux/slice";
import {
  createStylistAction,
  getStylistsAction,
  updateStylistWithChildrenAction,
} from "../../../features/Stylisation/redux/actions";
import {
  selectSelectedStylist,
  selectStylistLoading,
  selectStylists,
} from "../../../features/Stylisation/redux/selectors";
import {
  setSelectedStylistAction,
  setStylistAction,
} from "../../../features/Stylisation/redux/slice";
import type { StylistValue } from "../../../services/api/instances/stylistApi/stylistTypes";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { getServicePagePath } from "../../../services/navigation/navigateToRoute";
import { getComplianceObjectsAction } from "../../../services/redux/complianceSlice/actions";
import { selectCompliance } from "../../../services/redux/complianceSlice/selectors";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { getFilteredObjects } from "../../../shared/utils/objectStorageFunctions";
import { useTranslation } from "react-i18next";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";

export const useStylistPage = () => {
  const dispatch = useAppDispatch();
  const compliance = useAppSelector(selectCompliance);
  const selectedStylist = useAppSelector(selectSelectedStylist);
  const stylists = useAppSelector(selectStylists);
  const isLoading = useAppSelector(selectStylistLoading);
  const folderItems = useAppSelector(selectFolderItems);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const storage = useMemo(
    () => createStorageObjectApi<StylistValue>(stylists),
    [stylists],
  );

  const sidebarStylistItems = useMemo(() => {
    return storage.toMenuItems();
  }, [stylists]);

  useEffect(() => {
    if (!stylists) return;
    if (!compliance) {
      dispatch(getComplianceObjectsAction());
    }
    dispatch(getStylistsAction())
      .unwrap()
      .then((response: StorageObject<StylistValue>[]) => {
        if (id === undefined && selectedStylist) {
          navigate(getServicePagePath(selectedStylist.id, "stylizator"));
          if (selectedStylist?.value !== null) {
            dispatch(setSelectedStylistAction(selectedStylist));
            dispatch(toggleAccordion(selectedStylist.id));
          }
        } else {
          const activeStylistById = storage.findById(id as string);
          const activeStylist = activeStylistById || response[0];
          if (!activeStylist) return;
          navigate(getServicePagePath(activeStylist.id, "stylizator"));
          dispatch(
            setSelectedStylistAction(
              activeStylist as StorageObject<StylistValue>,
            ),
          );
          dispatch(toggleAccordion(activeStylist.id));
        }
      });
  }, []);

  const addStylist = () => {
    dispatch(clearRagObjects());
    const id = selectedStylist?.id;
    const folder = storage.findParent(id as string);
    switch (true) {
      case selectedStylist?.value === null || folder !== null: {
        const folderId = folder?.id ? folder?.id : id;
        dispatch(createStylistAction({ isFolder: "object" }))
          .unwrap()
          .then((response: StorageObject<StylistValue>) => {
            dispatch(
              updateStylistWithChildrenAction({
                childId: response.id,
                parentId: folderId as string,
              }),
            );
            const newStylist = storage.addItem(response, folderId);
            dispatch(setStylistAction(newStylist));
            dispatch(setSelectedStylistAction(response));
            navigate(getServicePagePath(response.id, "stylizator"));
            if (!folderItems.includes(folderId as string)) {
              dispatch(toggleAccordion(id as string));
            }
          });
        return;
      }
      case folder === null: {
        dispatch(createStylistAction({ isFolder: "object" }))
          .unwrap()
          .then((response: StorageObject<StylistValue>) => {
            const newStylists = [...stylists, response];
            dispatch(setStylistAction(newStylists));
            dispatch(setSelectedStylistAction(response));
            navigate(getServicePagePath(response.id, "stylizator"));
          });
        return;
      }
      default: {
        return;
      }
    }
  };

  const addFolder = (name?: string) => {
    const postObj: {
      isFolder: "folder";
      name?: string;
      withoutCount?: boolean;
    } =
      name === t("common.default_folder_name")
        ? { isFolder: "folder" }
        : { isFolder: "folder", name: name, withoutCount: true };
    dispatch(clearRagObjects());
    dispatch(createStylistAction(postObj))
      .unwrap()
      .then((response: StorageObject<StylistValue>) => {
        const newStylists = storage.addItem({ ...response, children: [] });
        dispatch(
          setStylistAction(getFilteredObjects<StylistValue>(newStylists)),
        );
        navigate(getServicePagePath(response.id, "stylizator"));
      });
  };

  const handleSelectStylist = (id: string) => {
    const stylist = storage.findById(id);
    dispatch(setSelectedStylistAction(stylist as StorageObject<StylistValue>));
    navigate(getServicePagePath(id, "stylizator"));
  };

  return {
    selectedStylist,
    sidebarStylistItems,
    isLoading,
    stylists,
    folderItems,
    addStylist,
    addFolder,
    handleSelectStylist,
  };
};
